import React from 'react'
import { MD, LG, XL } from '@zendeskgarden/react-typography'
import { Title, Well } from '@zendeskgarden/react-notifications'
import styled from 'styled-components'
import { Row, Col } from '@zendeskgarden/react-grid'
import { decideIncidentStatus, hasNonEmptyIncidentUpdates, localizeIncidentTimestamp } from '../../utils/incident'
import StatusIcon from './StatusIcon'

const StatusBannerWell = styled(Well)`
  width: 950px;
  border: none;
`

const SectionHeader = styled(MD)`
  font-weight: 600
  padding-left: 10px
  color: grey
  padding-right: 4px
  display:inline
`

const IncidentUpdatesTitle = styled(MD)`
  font-weight: 600
  color: grey
  display:inline
`

const InsideIncidentPadding = styled(Row)`
  padding-top: 8px
`

const StyledIcon = styled(MD) `
  padding-left: 10px
`

const IncidentsUpdateDate = styled(MD) `
  padding-left: 20px
`

const StyledDate = styled(XL)`
  padding-top: 32px
`

const ServiceList = styled(MD)`
  display: inline
`

const ServiceListGrouper = styled(Row)`
  display: inline
`

const StyledContent = styled.div`
  width: 630px
`

const IncidentName = styled(MD)`
  padding-left: 5px
  width: 600px
  margin: auto
`

const IncidentTitle = styled(Row)`
  padding-top: 12px
`

const StyledProductGrouping = styled(Row)`
  padding-left: 20px
`
const StyledServiceGrouping = styled(Col)`
  padding-left: 0
  padding-right: 0
`

const Postmortem = ({ postmortem }) => {
  return (
    postmortem ?
      <div>
        <a href={postmortem} target="_blank" rel="noopener noreferrer">View post mortem report</a>
      </div> :
      <div>
        <MD>A post-mortem will be provided in the <a href="https://support.zendesk.com/hc/en-us/sections/4405298854426-Service-notifications" target="_blank" rel="noopener noreferrer">Service Notifications</a> section when complete.</MD>
      </div>
  )
}

const DateHeader = ({ date }) => {
  return (
    <StyledDate>
      <div>
        <LG>{`${date}`}</LG>
      </div>
    </StyledDate>
  )
}

const renderProductsAndServices = incidentServicesForIncident => {
  {
    const productIncidentServices = incidentServicesForIncident.filter(i => i.attributes.serviceParentId === null)
    return productIncidentServices.map((productIncidentService, index) => {
      return <AffectedProductsAndService key={index} productIncidentService={productIncidentService} incidentServicesForIncident={incidentServicesForIncident} />
    })
  }
}

const AffectedProductsAndService = ({ productIncidentService, incidentServicesForIncident }) => {
  const parentId = parseInt(productIncidentService.attributes.serviceId)
  const childrenServiceNames = incidentServicesForIncident.filter(is => is.attributes.serviceParentId === parentId).map(is => is.attributes.serviceName).join(', ')

  return (
    <StyledProductGrouping key={productIncidentService.id}>
      <StyledServiceGrouping md="auto">
        <SectionHeader>
          {productIncidentService.attributes.serviceName}
          {childrenServiceNames && ': '}
        </SectionHeader>
      </StyledServiceGrouping>
      <StyledServiceGrouping>{childrenServiceNames}</StyledServiceGrouping>
    </StyledProductGrouping>
  )
}

const DaysIncidents = ({ incident, incidentServices, updates, inTimezone }) => {
  const incidentUpdates = incident.relationships.incidentUpdates.data.map(as => updates.find(s => s.id === as.id))
  const incidentServicesForIncident = incidentServices.filter(is => (is.attributes.incidentId.toString() === incident.id) && (is.attributes.internalTool === false))

  return (
    <div>
      <IncidentTitle>
        <StyledIcon><StatusIcon status={decideIncidentStatus(incident.attributes)}/></StyledIcon>
        <Title>
          <IncidentName>{incident.attributes.name}</IncidentName>
        </Title>
      </IncidentTitle>
      <Row>
        <SectionHeader>Date range</SectionHeader>
        <MD>{`${localizeIncidentTimestamp(incident.attributes.startedAt, inTimezone)} - ${localizeIncidentTimestamp(incident.attributes.resolvedAt, inTimezone)}`}</MD>
      </Row>
      <ServiceListGrouper>
        <SectionHeader>Affected Services</SectionHeader>
        <ServiceList>
          { renderProductsAndServices(incidentServicesForIncident) }
        </ServiceList>
      </ServiceListGrouper>
      <InsideIncidentPadding/>
      {incident.attributes && <Postmortem postmortem={incident.attributes.postmortem}/>}
      <InsideIncidentPadding/>
      <IncidentUpdatesTitle>{hasNonEmptyIncidentUpdates(incidentUpdates) && 'Incident updates'}</IncidentUpdatesTitle>
      <InsideIncidentPadding/>
      <dd>
        {incidentUpdates && incidentUpdates.map(update => {
          if (update.attributes.description) {
            return (
              <IncidentsUpdateDate key={`update-${  update.id}`}>
                <li style={{ textIndent: '-20px' }}>
                  {localizeIncidentTimestamp(update.attributes.createdAt, inTimezone)}
                  {' '}
                  {update.attributes.description}
                </li>
              </IncidentsUpdateDate>
            )
          }
        })}
      </dd>
    </div>
  )
}

const getCreateDatesList = (pastIncidents, inTimezone) => {
  let resolvedDate = null
  const dateHeaders = []

  {pastIncidents.map(incident => {
    resolvedDate = localizeIncidentTimestamp(incident.attributes.resolvedAt, inTimezone, false)
    if (dateHeaders.indexOf(resolvedDate) === -1) {
      dateHeaders.push(resolvedDate)
    }
  })
  }

  return dateHeaders
}

const filterIncidentsByResolvedAt = (incidents, resolvedDate, inTimezone) => {
  return incidents.filter(incident => {
    return localizeIncidentTimestamp(incident.attributes.resolvedAt, inTimezone, false) === resolvedDate
  })
}

const PastIncidents = ({ pastIncidents, incidentServices, updates, inTimezone }) => {
  const dateHeaders = getCreateDatesList(pastIncidents, inTimezone)
  let daysIncidents = null

  return (
    <StatusBannerWell id='past_incidents_section'>
      <Title>
        <XL>Recently Resolved Incidents</XL>
      </Title>
      {dateHeaders && dateHeaders.map((date, dayIndex) => {
        daysIncidents = filterIncidentsByResolvedAt(pastIncidents, date, inTimezone)
        return(
          <StyledContent key={`date-row-${dayIndex}`}>
            <DateHeader date={date} />
            {daysIncidents && daysIncidents.map((incident, incIndex) => {
              return (
                <div key={`incident-${incIndex}`}>
                  {incidentServices &&
                    <DaysIncidents
                      incident={incident}
                      incidentServices={incidentServices}
                      updates={updates}
                      inTimezone={inTimezone}
                    />
                  }
                </div>
              )
            })}
          </StyledContent>
        )
      })}
    </StatusBannerWell>
  )
}

export default PastIncidents
